/**
 * DEV-261 US-9 — Resumo (Figma 20186:22973). Montado só da definição e das
 * respostas (FR-018): seções na ordem das etapas visíveis, linha só com valor,
 * seção vazia some, Descritivos sempre, páginas adicionais numa seção própria
 * no fim. Rótulos próprios do resumo e formatos (faixa, mês por extenso,
 * Sim/Não, precedência estado × cidade) são apresentação.
 */
import type { CustomFormPage, FormDefinition, StandardFormPage } from './definition';
import { isQuestionShown, type Answers } from './form-rules';
import { isEmptyAnswer } from './answer-validation';
import { formatMoney } from './number-input';

export interface SummaryRow {
  question: string;
  label: string;
  value: string | string[];
  expandable?: boolean;
  /** Página onde a resposta vive (Editar das Perguntas adicionais). */
  pageName?: string;
}

export interface SummarySectionModel {
  key: string;
  title: string;
  /** Destino do "Editar". */
  pageName: string;
  rows: SummaryRow[];
  alwaysRender?: boolean;
}

const MONTHS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

/** `YYYY-MM` → "Dezembro de 2029" (data civil, sem `Date`). */
export function formatMonth(value: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  const month = m ? MONTHS[Number(m[2]) - 1] : undefined;
  return m && month ? `${month} de ${m[1]}` : value;
}

/** Rótulos que o Resumo escreve diferente do formulário (22973). */
const SUMMARY_LABEL: Record<string, string> = {
  tipo_divulgacao_salario: 'Divulgação do salário',
  aceita_todos_cursos: 'Aceita todos os cursos',
  valor_exato: 'Salário',
};

interface CityValue {
  uf: string;
  city: string;
}

const brl = (n: number) => `R$ ${formatMoney(n)}`;

function textOf(value: unknown): string | string[] | null {
  if (isEmptyAnswer(value)) return null;
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const items = value
      .map((v) => {
        if (typeof v === 'string' || typeof v === 'number') return String(v);
        if (v && typeof v === 'object') {
          const o = v as Record<string, unknown>;
          const label = o.label ?? o.name ?? o.text ?? o.city;
          return typeof label === 'string' ? label : null;
        }
        return null;
      })
      .filter((v): v is string => !!v);
    return items.length > 0 ? items : null;
  }
  return null;
}

function standardRows(
  page: StandardFormPage,
  answers: Answers,
  stateNames: Map<string, string>
): SummaryRow[] {
  const rows: SummaryRow[] = [];
  for (const q of page.questions) {
    if (!isQuestionShown(q.name, answers)) continue;
    const value = answers[q.name];
    const label = SUMMARY_LABEL[q.name] ?? q.title;

    if (q.name === 'valor_minimo') {
      const min = answers.valor_minimo;
      const max = answers.valor_maximo;
      if (typeof min === 'number' && typeof max === 'number') {
        rows.push({
          question: q.name,
          label: 'Faixa salarial',
          value: `${brl(min)} a ${brl(max)}`,
        });
      }
      continue;
    }
    if (q.name === 'valor_maximo') continue;
    if (q.name === 'valor_exato') {
      if (typeof value === 'number') rows.push({ question: q.name, label, value: brl(value) });
      continue;
    }
    if (q.name === 'local_trabalho') {
      // QA 08/10/2026: "Cidade - UF", como nos requisitos de localidade.
      const cities = Array.isArray(value) ? (value as (CityValue | string)[]) : [];
      // Valor legado em texto (gravado pela API) aparece como veio.
      const items = cities
        .map((c) => (typeof c === 'string' ? c : c && c.city ? `${c.city} - ${c.uf}` : null))
        .filter((c): c is string => !!c);
      if (items.length > 0) rows.push({ question: q.name, label, value: items });
      continue;
    }
    if (q.name === 'requisitos_localidade_cidade') continue;
    if (q.name === 'requisitos_localidade_estado') {
      const ufs = Array.isArray(value) ? (value as string[]) : [];
      const cities = Array.isArray(answers.requisitos_localidade_cidade)
        ? (answers.requisitos_localidade_cidade as CityValue[])
        : [];
      // Precedência (22593): a cidade substitui o próprio estado; os outros valem inteiros.
      const coveredByCity = new Set(cities.map((c) => c.uf));
      const items = [
        ...ufs.filter((uf) => !coveredByCity.has(uf)).map((uf) => stateNames.get(uf) ?? uf),
        ...cities.map((c) => `${c.city} - ${c.uf}`),
      ];
      if (items.length > 0)
        rows.push({ question: q.name, label: 'Requisitos de localidade', value: items });
      continue;
    }
    if (q.name === 'idade_minima' || q.name === 'idade_maxima') {
      // Resumo (22973): "18 anos".
      if (typeof value === 'number') rows.push({ question: q.name, label, value: `${value} anos` });
      continue;
    }
    if (q.name === 'data_minima_formacao' || q.name === 'data_maxima_formacao') {
      if (typeof value === 'string' && value)
        rows.push({ question: q.name, label, value: formatMonth(value) });
      continue;
    }
    if (page.name === 'descritivos') {
      rows.push({
        question: q.name,
        label,
        value: typeof value === 'string' ? value : '',
        expandable: true,
      });
      continue;
    }
    const text = textOf(value);
    if (text !== null) rows.push({ question: q.name, label, value: text });
  }
  return rows;
}

interface SurveyNode {
  name?: string;
  title?: string;
  elements?: SurveyNode[];
}

/** Título da pergunta, procurando também dentro de painéis. */
function customQuestionTitle(page: CustomFormPage, question: string): string {
  const find = (nodes: SurveyNode[] | undefined): SurveyNode | undefined => {
    for (const n of nodes ?? []) {
      if (n.name === question && !n.elements) return n;
      const inner = find(n.elements);
      if (inner) return inner;
    }
    return undefined;
  };
  const el = find(page.survey.pages as SurveyNode[] | undefined);
  return el?.title ?? el?.name ?? question;
}

const OTHER = 'other';
const COMMENT_SUFFIX = '-Comment';

/** Resposta "outro" do SurveyJS: troca o valor `other` pelo texto digitado. */
function withOtherText(value: unknown, comment: unknown): unknown {
  const text = typeof comment === 'string' && comment.trim() !== '' ? comment.trim() : null;
  if (!text) return value;
  if (value === OTHER) return text;
  if (Array.isArray(value)) return value.map((v) => (v === OTHER ? text : v));
  return value;
}

export function buildSummary(
  def: FormDefinition,
  answers: Answers,
  opts: { stateNames: Map<string, string> }
): SummarySectionModel[] {
  const sections: SummarySectionModel[] = [];
  const customRows: SummaryRow[] = [];
  let firstCustomPage: string | null = null;

  for (const page of def.pages) {
    if (page.kind === 'custom') {
      firstCustomPage ??= page.name;
      for (const q of page.questionNames) {
        // O texto do "outro" entra no lugar do valor, na linha da própria pergunta.
        if (q.endsWith(COMMENT_SUFFIX)) continue;
        const text = textOf(withOtherText(answers[q], answers[`${q}${COMMENT_SUFFIX}`]));
        if (text !== null) {
          customRows.push({
            question: q,
            label: customQuestionTitle(page, q),
            value: text,
            pageName: page.name,
          });
        }
      }
      continue;
    }
    const rows = standardRows(page, answers, opts.stateNames);
    const always = page.name === 'descritivos';
    if (rows.length > 0 || always) {
      sections.push({
        key: page.name,
        title: page.title,
        pageName: page.name,
        rows,
        alwaysRender: always,
      });
    }
  }

  if (customRows.length > 0 && firstCustomPage) {
    sections.push({
      key: 'custom',
      title: 'Perguntas adicionais',
      // AC-68: o Editar leva à página onde o campo respondido vive — com mais de
      // uma página adicional, não necessariamente a primeira. O Figma tem um só
      // Editar por seção: vai à página do primeiro campo respondido (D23).
      pageName: customRows[0]?.pageName ?? firstCustomPage,
      rows: customRows,
    });
  }
  return sections;
}
